import React, { useEffect, useState } from "react";
import styled from "styled-components";

/* ===== API base (inline, per your pattern) ===== */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;

/* Minimal HTTP helper */
async function http(path, { method = "GET", data, headers, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: data ? JSON.stringify(data) : undefined,
    signal,
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

/* Local API (page-only) — matches backend exactly */
const api = {
  list: (params = {}) => {
    const usp = new URLSearchParams(params);
    const qs = usp.toString() ? `?${usp.toString()}` : "";
    return http(`/api/agent-posts${qs}`);
  },
  create: (payload) => http(`/api/agent-posts`, { method: "POST", data: payload }),
  update: (id, payload) => http(`/api/agent-posts/${id}`, { method: "PUT", data: payload }),
  remove: (id) => http(`/api/agent-posts/${id}`, { method: "DELETE" }),
};

const theme = {
  brand: "#3dccc7",
  brandDark: "#2a5a7a",
  text: "#f1f1f1",
  subtext: "#d6d9de",
  bg: "#0b1320",
  card: "#0f1b2d",
  border: "#1e293b",
};

const Page = styled.div`display: grid; gap: 16px; color: ${theme.text};`;
const Title = styled.h2`margin: 0; font-weight: 800;`;
const Row = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap;
`;
const Button = styled.button`
  background: ${theme.brand}; color: #fff; border: 0; padding: 10px 14px; border-radius: 10px; font-weight: 700; cursor: pointer;
  &:hover{ background: ${theme.brandDark}; }
`;
const Card = styled.div`
  background: ${theme.card}; border: 1px solid ${theme.border}; border-radius: 14px; overflow: hidden;
`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; color: ${theme.text};
  th, td { border-bottom: 1px solid ${theme.border}; padding: 12px; text-align: left; vertical-align: top; }
  th { background: rgba(255,255,255,0.03); }
`;
const Badge = styled.span`
  padding: 2px 8px; border-radius: 999px; font-size: 12px; border: 1px solid ${theme.border};
  background: ${p=>p.$status==='published' ? 'rgba(46,204,113,.15)' : 'rgba(255,145,77,.15)'};
`;

/* ===== Modal (updated) ===== */
const ModalWrap = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.5);
  display: grid;
  place-items: center;
  z-index: 1000;

  /* allow overlay to scroll if card grows taller than viewport */
  padding: 24px;
  overflow: auto;
  overscroll-behavior: contain;
`;

const ModalCard = styled.div`
  width: min(820px, 92vw);
  background: ${theme.card};
  border: 1px solid ${theme.border};
  border-radius: 16px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* keeps rounded corners clean */
`;

const ModalBody = styled.div`
  display: grid;
  gap: 12px;
  overflow: auto;    /* key: scrollable area */
  padding: 16px;
  padding-right: 18px; /* room for scrollbar */
`;

const Grid = styled.div`display: grid; gap: 12px;`;
const Field = styled.label`display: grid; gap: 6px; font-size: 14px;`;
const Input = styled.input`
  padding: 10px 12px; border-radius: 10px; border: 1px solid ${theme.border}; background: #0c1626; color: ${theme.text};
`;
const Textarea = styled.textarea`
  min-height: 160px; padding: 12px; border-radius: 12px; border: 1px solid ${theme.border}; background: #0c1626; color: ${theme.text}; resize: vertical;
`;
const Actions = styled.div`display: flex; gap: 10px; justify-content: flex-end;`;

/* NEW: one-line content cell */
const ContentCell = styled.span`
  display: inline-block; max-width: 380px;
  overflow: hidden; white-space: nowrap; text-overflow: ellipsis;
  cursor: pointer; color: ${theme.subtext};
  &:hover { text-decoration: underline; color: #ffffff; }
`;

/* NEW: lightweight divider inside preview modal */
const Hr = styled.hr`border:0; border-top:1px solid ${theme.border}; margin: 12px 0;`;

export default function AgentsManager(){
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);     // create/edit modal
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", youtubeUrl: "", status: "published" });
  const [error, setError] = useState("");

  const [preview, setPreview] = useState(null); // preview modal row

  async function load(){
    setLoading(true);
    try{ const res = await api.list(); setRows(res.data || []); }
    finally{ setLoading(false); }
  }
  useEffect(()=>{ load(); },[]);

  /* Lock body scroll when any modal open */
  useEffect(() => {
    const anyModalOpen = open || !!preview;
    const { body } = document;
    const prev = body.style.overflow;
    if (anyModalOpen) body.style.overflow = 'hidden';
    else body.style.overflow = prev || '';
    return () => { body.style.overflow = prev || ''; };
  }, [open, preview]);

  /* Esc to close */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (open) setOpen(false);
        if (preview) setPreview(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, preview]);

  function openCreate(){
    setEditing(null);
    setForm({ title: "", content: "", youtubeUrl: "", status: "published" });
    setError(""); setOpen(true);
  }
  function openEdit(row){
    setEditing(row);
    setForm({
      title: row.title,
      content: row.content,
      youtubeUrl: row.youtubeUrl || "",
      status: row.status,
    });
    setError(""); setOpen(true);
  }

  async function handleSave(){
    try{
      setError("");
      const payload = { ...form };
      if (!payload.title?.trim() || !payload.content?.trim()){
        setError("Title and Content are required"); return;
      }

      if (editing) {
        // Auto-update postedAt on edit
        payload.postedAt = new Date().toISOString();
        await api.update(editing._id, payload);
      } else {
        await api.create(payload);
      }

      setOpen(false);
      await load();
    } catch(e){ setError(e.message || "Failed to save"); }
  }

  async function handleDelete(row){
    if (!window.confirm(`Delete post: ${row.title}?`)) return;
    try{ await api.remove(row._id); await load(); }
    catch(e){ alert(e.message || "Failed to delete"); }
  }

  const fmt = (d)=> d ? new Date(d).toLocaleDateString(undefined,{year:'numeric', month:'short', day:'numeric'}) : "";

  return (
    <Page>
      <Row>
        <Title>AI Agents — Posts</Title>
        <Button onClick={openCreate}>+ Add New</Button>
      </Row>

      <Card>
        <Table>
          <thead>
            <tr>
              <th style={{width:40}}>#</th>
              <th>Title</th>
              <th>Posted</th>
              <th>Status</th>
              <th>Content</th>
              <th>YouTube</th>
              <th style={{width:180}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{padding:20}}>Loading…</td></tr>}
            {!loading && rows.length===0 && <tr><td colSpan={7} style={{padding:20}}>No posts yet.</td></tr>}
            {!loading && rows.map((r, idx)=> (
              <tr key={r._id}>
                <td>{idx+1}</td>
                <td>{r.title}</td>
                <td>{fmt(r.postedAt)}</td>
                <td><Badge $status={r.status}>{r.status}</Badge></td>
                <td>
                  <ContentCell onClick={()=>setPreview(r)} title="Click to preview">
                    {r.content || ""}
                  </ContentCell>
                </td>
                <td>
                  {r.youtubeUrl ? (
                    <a href={r.youtubeUrl} target="_blank" rel="noopener noreferrer" style={{color:'#93c5fd'}}>
                      Open link ↗
                    </a>
                  ) : "—"}
                </td>
                <td>
                  <div style={{display:'flex', gap:8}}>
                    <Button onClick={()=>openEdit(r)}>Edit</Button>
                    <Button style={{background:'#b91c1c'}} onClick={()=>handleDelete(r)}>Delete</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      {/* Create / Edit modal */}
      {open && (
        <ModalWrap
          onClick={(e)=>{ if(e.target===e.currentTarget) setOpen(false); }}
          role="dialog" aria-modal="true" aria-labelledby="edit-modal-title"
        >
          <ModalCard>
            <Row style={{padding:'16px 16px 0'}}>
              <h3 id="edit-modal-title" style={{margin:0}}>
                {editing ? 'Edit Post' : 'Create Post'}
              </h3>
            </Row>

            <ModalBody>
              <Field><span>Title *</span>
                <Input value={form.title} onChange={e=>setForm(f=>({...f, title:e.target.value}))} placeholder="AI Agent for Submittals" />
              </Field>

              <Field><span>YouTube URL (optional)</span>
                <Input value={form.youtubeUrl} onChange={e=>setForm(f=>({...f, youtubeUrl:e.target.value}))} placeholder="https://youtu.be/VIDEO_ID" />
              </Field>

              {/* Posted date removed — handled automatically */}
              <Field><span>Content *</span>
                <Textarea value={form.content} onChange={e=>setForm(f=>({...f, content:e.target.value}))} placeholder={`Write the guide...`} />
              </Field>

              <Field>
                <span>Status</span>
                <select
                  style={{padding:'10px 12px', borderRadius:10, border:`1px solid ${theme.border}`, background:'#0c1626', color:theme.text}}
                  value={form.status}
                  onChange={e=>setForm(f=>({...f, status:e.target.value}))}
                >
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </Field>

              {error && <div style={{color:'#ffb4a6'}}>{error}</div>}
            </ModalBody>

            <Actions style={{padding:'0 16px 16px', marginTop: '8px'}}>
              <Button onClick={()=>setOpen(false)} style={{background:'#6b7280'}}>Cancel</Button>
              <Button onClick={handleSave}>{editing? 'Save Changes' : 'Create'}</Button>
            </Actions>
          </ModalCard>
        </ModalWrap>
      )}

      {/* Preview modal (read-only) */}
      {preview && (
        <ModalWrap
          onClick={(e)=>{ if(e.target===e.currentTarget) setPreview(null); }}
          role="dialog" aria-modal="true" aria-labelledby="preview-modal-title"
        >
          <ModalCard>
            <Row style={{padding:'16px 16px 0'}}>
              <h3 id="preview-modal-title" style={{margin:0}}>Preview — {preview.title}</h3>
            </Row>

            <ModalBody>
              <div style={{whiteSpace:'pre-wrap', lineHeight:1.6}}>
                {preview.content}
              </div>
              <Hr/>
              <div>
                <strong>Video: </strong>
                {preview.youtubeUrl ? (
                  <a href={preview.youtubeUrl} target="_blank" rel="noopener noreferrer" style={{color:'#93c5fd'}}>
                    {preview.youtubeUrl}
                  </a>
                ) : "—"}
              </div>
            </ModalBody>

            <Actions style={{padding:'0 16px 16px'}}>
              <Button onClick={()=>setPreview(null)} style={{background:'#6b7280'}}>Close</Button>
            </Actions>
          </ModalCard>
        </ModalWrap>
      )}
    </Page>
  );
}
